/*
 * Copyright (c) Microsoft Corporation. All rights reserved. Licensed under the MIT license.
 * See LICENSE in the project root for license information.
 */

/* global document, Office */
import NavigationHeaderDirective from './directives/NavigationHeaderDirective';
import ErrorDirective from './directives/ErrorDirective';
import ErrorDirectiveV2 from './directives/ErrorDirectiveV2';
import SnackBarDirective from './directives/SnackBarDirective';
import UpgradeSubscriptionDialogDirective from './directives/DrawpUpgradeSubscriptionDirective';
import AddOnFactory from './factories/addonFactory';
import DrawpSentenceFramesDiretive from './directives/DrawpSentenceFramesViewDirective';
import DrawpBookItemViewDirective from './directives/DrawpBookItemViewDirective';
import DrawpLanguageFramesDirective from './directives/DrawpLanguageSelectorDirective';
import DrawpProgress from './directives/ProgressDirective';
import DomObserverDirective from './directives/DOMObserverDirective';
import DrawpLoginDialogDirective from './directives/DrawpLoginDialogDirective';

import VpMathJournalsController from './controllers/VpMathJournalsController';
import VpMathJournalsTemplate from './templates/vp-math-journals-template.html';

import GraphicOrganizersController from './controllers/GraphicOrganizersController';
import GraphicOrganizersTemplate from './templates/graphic-organizers.html';

import MultiStateAddonIndexController from './controllers/MultiStateAddonIndexController';
import MultuStateAddonTemplate from './templates/multi-state-addon-index.html';

import Lvl3ScaffoldingController from './controllers/Lvl3ScaffoldingController';
import Lvl3ScaffoldingTemplate from './templates/lvl3-scaffolding.html';

import AboutController from './controllers/AboutController';
import AboutTemplate from './templates/about-template.html';

import PaymentController from './controllers/PaymentController';
import PaymentTemplate from './templates/payment-template.html';

import SubscriptionsController from './controllers/SubscriptionsController';
import SubscriptionsTemplate from './templates/subscriptions-template.html';

import Lvl4ScaffoldingController from './controllers/Lvl4ScaffoldingController';
import Lvl4ScaffoldingTemplate from './templates/lvl4-scaffolding.html';

import Lvl6ScaffoldingController from './controllers/Lvl6ScaffoldingController';
import Lvl6ScaffoldingTemplate from './templates/lvl6-scaffolding.html';

import SlideTranslationController from './controllers/SlideTranslationController';
import SlideTranslationTemplate from './templates/slide-translation.html';

import DrawpService from './services/DrawpService';
import config from 'Config';

// Initialize Sentry via CDN (Zero npm required)
if (window.Sentry) {
  if (config.sentryDsn) {
    window.Sentry.init({
      dsn: config.sentryDsn,
      environment: config.env || 'development',
      tracesSampleRate: 1.0,
      beforeSend(event) {
        if (window.Office && window.Office.context) {
          event.tags = event.tags || {};
          event.tags.host = window.Office.context.host;
          event.tags.platform = window.Office.context.platform;
        }
        return event;
      }
    });
  } else {
    console.info("[Sentry] No sentryDsn provided in config. Error monitoring is idle.");
  }
}

const multiStateAddonApp = angular.module('MultiStateAddonApp', ['ui.router', 'ui.router.state.events', 'ngSanitize', 'ngStorage']);

// AngularJS Exception Handler to forward unhandled errors to Sentry
multiStateAddonApp.factory('$exceptionHandler', ['$log', function ($log) {
  return function (exception, cause) {
    $log.error(exception, cause);
    if (window.Sentry && typeof window.Sentry.captureException === 'function') {
      window.Sentry.withScope(function (scope) {
        if (cause) {
          scope.setExtra('cause', cause);
        }
        window.Sentry.captureException(exception);
      });
    }
  };
}]);

multiStateAddonApp
  .config(['$httpProvider', function ($httpProvider) {
    $httpProvider.defaults.headers.common = {};
    $httpProvider.defaults.headers.post = {};
    $httpProvider.defaults.headers.put = {};
    $httpProvider.defaults.headers.patch = {};
  }])
  .run(['$rootScope', function ($rootScope) {
    $rootScope.$on('$stateChangeSuccess', function (event, toState, toParams, fromState, fromParams) {
      $rootScope.previousState = fromState;
      $rootScope.previousParams = fromParams;
    });
  }]);

multiStateAddonApp.factory('addonFactory', AddOnFactory);
multiStateAddonApp.service('DrawpService', DrawpService);

multiStateAddonApp.directive('drawpNavHeader', NavigationHeaderDirective);
multiStateAddonApp.directive('drawpError', ErrorDirective);
multiStateAddonApp.directive('drawpErrorV2', ErrorDirectiveV2);
multiStateAddonApp.directive('drawpSnackBar', SnackBarDirective);
multiStateAddonApp.directive('drawpUpgradeSubsctiptionDialog', UpgradeSubscriptionDialogDirective);
multiStateAddonApp.directive('drawpSentenceFrames', DrawpSentenceFramesDiretive);
multiStateAddonApp.directive('drawpBookItemView', DrawpBookItemViewDirective);
multiStateAddonApp.directive('drawpLanguageSelector', DrawpLanguageFramesDirective);
multiStateAddonApp.directive('drawpProgress', DrawpProgress);
multiStateAddonApp.directive('domObserver', DomObserverDirective);
multiStateAddonApp.directive('drawpLoginDialog', DrawpLoginDialogDirective);

multiStateAddonApp.controller("MultiStateAddonIndexController", MultiStateAddonIndexController);
multiStateAddonApp.controller("Lvl3ScaffoldingController", Lvl3ScaffoldingController);
multiStateAddonApp.controller("AboutController", AboutController);
multiStateAddonApp.controller("GraphicOrganizersController", GraphicOrganizersController);
multiStateAddonApp.controller("VpMathJournalsController", VpMathJournalsController);
multiStateAddonApp.controller('PaymentController', PaymentController);
multiStateAddonApp.controller('SubscriptionsController', SubscriptionsController);
multiStateAddonApp.controller('Lvl4ScaffoldingController', Lvl4ScaffoldingController);
multiStateAddonApp.controller('Lvl6ScaffoldingController', Lvl6ScaffoldingController);
multiStateAddonApp.controller('SlideTranslationController', SlideTranslationController);

multiStateAddonApp.component('addonAppIndex', {
  template: MultuStateAddonTemplate,
  controller: 'MultiStateAddonIndexController'
});

multiStateAddonApp.component('scaffoldingLevel3', {
  template: Lvl3ScaffoldingTemplate,
  controller: 'Lvl3ScaffoldingController'
});

multiStateAddonApp.component('aboutPage', {
  template: AboutTemplate,
  controller: 'AboutController'
});

multiStateAddonApp.component('graphicOrganizers', {
  template: GraphicOrganizersTemplate,
  controller: 'GraphicOrganizersController'
});

multiStateAddonApp.component('vpMathJournals', {
  template: VpMathJournalsTemplate,
  controller: 'VpMathJournalsController'
});

multiStateAddonApp.component('payment', {
  template: PaymentTemplate,
  controller: 'PaymentController'
});

multiStateAddonApp.component('activeSubscriptionsList', {
  template: SubscriptionsTemplate,
  controller: SubscriptionsController
});

multiStateAddonApp.component('scaffoldingLevel4', {
  template: Lvl4ScaffoldingTemplate,
  controller: 'Lvl4ScaffoldingController'
});

multiStateAddonApp.component('scaffoldingLevel6', {
  template: Lvl6ScaffoldingTemplate,
  controller: 'Lvl6ScaffoldingController'
});

multiStateAddonApp.component('slideTranslation', {
  template: SlideTranslationTemplate,
  controller: 'SlideTranslationController'
});

multiStateAddonApp.config(($stateProvider, $urlRouterProvider) => {

  const addonAppIndexState = {
    name: 'addonAppIndex',
    url: '/index',
    component: 'addonAppIndex'
  };

  const scaffoldingLevel3State = {
    name: 'scaffoldingLevel3',
    url: '/scaff-lvl-3',
    component: 'scaffoldingLevel3'
  };

  const aboutState = {
    name: 'about',
    url: '/about',
    component: 'aboutPage'
  };

  const graphicOrganizersState = {
    name: 'graphicOrganizers',
    url: '/graphic-organizers',
    component: 'graphicOrganizers'
  };

  const vpMathJournals = {
    name: 'vpMathJournals',
    url: '/vp-math-journals',
    component: 'vpMathJournals'
  };

  const payment = {
    name: 'payment',
    params: {
      language: null,
      tool: null,
      data1: null,
      data2: null,
    },
    url: '/payment',
    component: 'payment'
  };

  const activeSubscriptionsList = {
    name: 'activeSubscriptionsList',
    url: '/active-subscriptions-list',
    component: 'activeSubscriptionsList'
  };

  const scaffoldingLevel4State = {
    name: 'scaffoldingLevel4',
    url: '/scaff-lvl-4',
    component: 'scaffoldingLevel4'
  };

  const scaffoldingLevel6State = {
    name: 'scaffoldingLevel6',
    url: '/scaff-lvl-6',
    component: 'scaffoldingLevel6'
  };

  const slideTranslationState = {
    name: 'slideTranslation',
    url: '/slide-translation',
    component: 'slideTranslation'
  };

  $stateProvider.state(addonAppIndexState);
  $stateProvider.state(scaffoldingLevel3State);
  $stateProvider.state(aboutState);
  $stateProvider.state(graphicOrganizersState);
  $stateProvider.state(vpMathJournals);
  $stateProvider.state(payment);
  $stateProvider.state(activeSubscriptionsList);
  $stateProvider.state(scaffoldingLevel4State);
  $stateProvider.state(scaffoldingLevel6State);
  $stateProvider.state(slideTranslationState);

  $urlRouterProvider.otherwise('/index');
});

Office.onReady(function (info) {
  console.log("Office.js is ready");
  if (window.Sentry && info) {
    if (info.host) window.Sentry.setTag("office_host", info.host);
    if (info.platform) window.Sentry.setTag("office_platform", info.platform);
  }

  // Trigger test error for Sentry verification
  if (window.Sentry) {
    console.log("[Sentry] Sending test error to Sentry dashboard...");
    window.Sentry.captureException(new Error("Test Error: Sentry integration verified in Drawp Office Add-in!"));
  }
});